import { Alert } from '../../components/atoms/Alert'
import { Badge } from '../../components/atoms/Badge'
import { Select } from '../../components/atoms/Select'
import { Spinner } from '../../components/atoms/Spinner'
import { Paginator } from '../../components/molecules/Paginator'
import { TableShell } from '../../components/molecules/TableShell'
import { Th, Td } from '../../components/molecules/Table'
import { StockInOutCharts } from '../warehouse/StockInOutCharts'
import {
  useRawMaterialStockHistory,
  type MaterialMovementDto
} from '../../../viewmodels/useRawMaterialStockHistory'

/**
 * /manufacturing/raw-material-stock-history — per-material stock ledger.
 * Mirror of warehouse.variant-stock-history (charts omitted in v1; ledger
 * carries the core "history maintained" requirement).
 */
export function RawMaterialStockHistoryPage(): JSX.Element {
  const vm = useRawMaterialStockHistory()

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-h2 text-charcoal">Raw Material Stock History</h1>
        <div className="w-full sm:w-auto sm:min-w-[16rem]">
          <Select
            value={vm.materialId ?? ''}
            onChange={(e) => vm.setMaterialId(e.target.value === '' ? null : e.target.value)}
            aria-label="Material"
          >
            <option value="">— Select a material —</option>
            {vm.materials.map((m) => (
              <option key={m.raw_material_id} value={m.raw_material_id}>
                {m.raw_material_name}{m.unit_label !== null ? ` (${m.unit_label})` : ''}
              </option>
            ))}
          </Select>
        </div>
      </header>

      {vm.error !== null ? (
        <Alert tone="error">
          {vm.error}{' '}
          <button type="button" className="underline underline-offset-2 hover:text-charcoal" onClick={() => vm.reload()}>
            Retry
          </button>
        </Alert>
      ) : null}

      {vm.materialId === null ? (
        <Placeholder>Pick a material above to see its stock movements and charts.</Placeholder>
      ) : (
        <>
          <StockInOutCharts
            year={vm.chart.year}
            monthly={vm.chart.monthly}
            quarterly={vm.chart.quarterly}
            isLoading={vm.chart.isLoading}
            error={vm.chart.error}
            onRetry={vm.chart.reload}
          />

          {vm.isLoading ? (
            <div className="flex items-center justify-center py-12"><Spinner /></div>
          ) : vm.ledger.length === 0 ? (
            <Placeholder>No stock movements recorded for this material yet.</Placeholder>
          ) : (
            <>
              <LedgerTable items={vm.ledger} />
              <Paginator
                page={vm.page}
                pageSize={vm.pageSize}
                total={vm.total}
                onPageChange={vm.setPage}
                disabled={vm.isLoading}
              />
            </>
          )}
        </>
      )}
    </div>
  )
}

function Placeholder({ children }: { readonly children: React.ReactNode }): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-body text-body-sm text-text-secondary">{children}</p>
    </div>
  )
}

const TYPE_LABEL: Record<string, string> = {
  stocked: 'Stocked',
  returned: 'Returned',
  consumed: 'Consumed'
}

function LedgerTable({ items }: { readonly items: ReadonlyArray<MaterialMovementDto> }): JSX.Element {
  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th>Date</Th>
            <Th>Movement</Th>
            <Th className="text-right">Qty</Th>
            <Th>Note</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((m) => {
            const isIn = m.direction === 'in'
            return (
              <tr key={m.id} className="border-t border-border">
                <Td className="whitespace-nowrap text-text-secondary">{formatDate(m.occurred_at)}</Td>
                <Td>
                  <Badge tone={isIn ? 'success' : 'error'}>{isIn ? 'In' : 'Out'}</Badge>
                  <span className="ml-2 text-text-secondary">{TYPE_LABEL[m.movement_type] ?? m.movement_type}</span>
                </Td>
                <Td className={`text-right whitespace-nowrap font-mono ${isIn ? 'text-forest' : 'text-terra'}`}>
                  {isIn ? '+' : '−'}{m.quantity_units}
                  {m.unit_label !== null ? <span className="ml-1 text-text-secondary text-body-xs">{m.unit_label}</span> : null}
                </Td>
                <Td className="text-text-secondary">{m.note ?? '—'}</Td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </TableShell>
  )
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}
